import {
  IntervalJSON,
  RelativeTimeRangeConfig,
} from '@highimpact-frontend/api-types';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatRelativeDivisionPreview,
  inferDivideIntoSubintervals,
} from './divisionPreview';
import InputMultiType from './InputMultiType';
import {
  buildRelativeTimeRanges,
  parseDefaultRelativeIntervals,
} from './RelativeTimeRange';

type RelativeTimeRangeProps = {
  onChange: (range: IntervalJSON[]) => void;
  noDivision?: boolean;
  noRelativeFuture?: boolean;
  defaultIntervals?: IntervalJSON[];
};

export default function RelativeTimeRangeForm({
  onChange,
  noDivision = false,
  noRelativeFuture = true,
  defaultIntervals,
}: RelativeTimeRangeProps) {
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  const parsed = useMemo(() => {
    const result = parseDefaultRelativeIntervals(defaultIntervals);
    return result;
  }, [defaultIntervals]);

  const [quantity, setQuantity] = useState<number>(
    parsed ? parsed.quantity : 1
  );
  const [unit, setUnit] = useState<RelativeTimeRangeConfig['unit']>(
    parsed ? parsed.unit : 'days'
  );

  const [includeFuture, setIncludeFuture] = useState(
    !noRelativeFuture && Boolean(parsed?.quantityFuture)
  );
  const [quantityFuture, setQuantityFuture] = useState<number>(
    parsed ? parsed.quantityFuture ?? 1 : 1
  );
  const [unitFuture, setUnitFuture] = useState<RelativeTimeRangeConfig['unit']>(
    parsed ? parsed.unitFuture ?? parsed.unit : 'days'
  );

  const initialDivideIntoSubintervals = useMemo(
    () => inferDivideIntoSubintervals(defaultIntervals),
    [defaultIntervals]
  );
  const [divideIntoSubintervals, setDivideIntoSubintervals] = useState(
    initialDivideIntoSubintervals
  );
  const effectiveNoDivision = noDivision || !divideIntoSubintervals;

  const { t } = useTranslation('configNav');

  const divisionPreview = useMemo(
    () =>
      formatRelativeDivisionPreview({
        quantity,
        unit,
        divided: !effectiveNoDivision,
        includeFuture: !noRelativeFuture && includeFuture,
        quantityFuture,
        unitFuture,
      }),
    [
      quantity,
      unit,
      effectiveNoDivision,
      noRelativeFuture,
      includeFuture,
      quantityFuture,
      unitFuture,
    ]
  );

  const handleChange = useCallback(
    (
      _quantity: typeof quantity,
      _unit: typeof unit,
      isFuture = false,
      includeFutureOverride?: boolean
    ) => {
      const q = Math.max(1, Math.floor(_quantity));
      const effectiveIncludeFuture = includeFutureOverride ?? includeFuture;
      const ownTimeRanges = buildRelativeTimeRanges(
        q,
        _unit,
        effectiveNoDivision,
        isFuture ? 'after' : 'before'
      );
      const otherTimeRanges = noRelativeFuture
        ? []
        : isFuture
        ? buildRelativeTimeRanges(quantity, unit, effectiveNoDivision, 'before')
        : buildRelativeTimeRanges(
            quantityFuture,
            unitFuture,
            effectiveNoDivision,
            'after'
          );
      const result = isFuture
        ? otherTimeRanges.concat(ownTimeRanges)
        : !noRelativeFuture && effectiveIncludeFuture
        ? ownTimeRanges.concat(otherTimeRanges)
        : ownTimeRanges;

      onChange(result);
    },
    [
      effectiveNoDivision,
      onChange,
      quantity,
      quantityFuture,
      unit,
      unitFuture,
      includeFuture,
      noRelativeFuture,
    ]
  );

  useEffect(() => {
    setDivideIntoSubintervals(initialDivideIntoSubintervals);
  }, [initialDivideIntoSubintervals]);

  const skipDivisionReEmitRef = useRef(true);
  useEffect(() => {
    if (skipDivisionReEmitRef.current) {
      skipDivisionReEmitRef.current = false;
      return;
    }
    handleChange(quantity, unit);
  }, [effectiveNoDivision, handleChange, quantity, unit]);

  useEffect(() => {
    if (!noRelativeFuture) {
      return;
    }

    if (!parsed?.quantityFuture) {
      return;
    }

    setIncludeFuture(false);
    handleChange(quantity, unit, false, false);
  }, [handleChange, noRelativeFuture, parsed?.quantityFuture, quantity, unit]);

  const showDivisionPreview = useMemo(() => {
    return !effectiveNoDivision && divisionPreview && quantity > 1;
  }, [effectiveNoDivision, divisionPreview, quantity]);

  return (
    <Box className="nav-form-group with-space">
      <Stack spacing={2}>
        <Card
          elevation={0}
          sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Typography component="h5" variant="h6" sx={{ fontSize: '100%' }}>
                Données mesurées
              </Typography>

              <InputMultiType
                type={'number'}
                label={t('Quantité')}
                name="quantity"
                required
                value={String(quantity)}
                min={1}
                step={1}
                inputMode="numeric"
                onChange={(value) => {
                  const v = parseInt(value, 10);
                  const normalizedV = Number.isFinite(v) && v > 0 ? v : 1;
                  setQuantity(normalizedV);
                  handleChange(normalizedV, unit);
                }}
              />

              <FormControl>
                <InputLabel id="unit-label">Unité</InputLabel>
                <Select
                  required
                  labelId="unit-label"
                  value={unit}
                  label="Unité"
                  onChange={(e) => {
                    const newUnit = e.target
                      .value as RelativeTimeRangeConfig['unit'];
                    setUnit(newUnit);
                    handleChange(quantity, newUnit);
                  }}
                >
                  <MenuItem value="millisecondes">Milliseconde(s)</MenuItem>
                  <MenuItem value="seconds">Seconde(s)</MenuItem>
                  <MenuItem value="minutes">Minute(s)</MenuItem>
                  <MenuItem value="hours">Heure(s)</MenuItem>
                  <MenuItem value="days">Jour(s)</MenuItem>
                  <MenuItem value="weeks">Semaine(s)</MenuItem>
                  <MenuItem value="months">Mois</MenuItem>
                  <MenuItem value="years">Année(s)</MenuItem>
                </Select>
              </FormControl>

              {!noDivision && (
                <>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Typography variant="body2">
                      Diviser en sous-intervalles
                    </Typography>
                    <Switch
                      checked={divideIntoSubintervals}
                      onChange={(_, checked) => {
                        setDivideIntoSubintervals(checked);
                      }}
                    />
                  </Stack>
                  {showDivisionPreview && (
                    <Typography variant="body2" color="text.secondary">
                      {divisionPreview}
                    </Typography>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {!noRelativeFuture && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Stack spacing={0.5}>
                    <Typography
                      component="h5"
                      variant="h6"
                      sx={{ fontSize: '100%' }}
                    >
                      Données prévisionnelles
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Uniquement pour les données avec modèle prédictif
                    </Typography>
                  </Stack>
                  <Switch
                    checked={includeFuture}
                    onChange={(_, checked) => {
                      setIncludeFuture(checked);
                      handleChange(quantity, unit, false, checked);
                    }}
                  />
                </Stack>

                <InputMultiType
                  type={'number'}
                  label={t('Quantité')}
                  name="quantity-future"
                  required={includeFuture}
                  value={String(quantityFuture)}
                  min={1}
                  step={1}
                  inputMode="numeric"
                  onChange={(value) => {
                    const v = parseInt(value, 10);
                    const normalizedV = Number.isFinite(v) && v > 0 ? v : 1;
                    setQuantityFuture(normalizedV);
                    handleChange(normalizedV, unitFuture, true);
                  }}
                  disabled={!includeFuture}
                />

                <FormControl disabled={!includeFuture}>
                  <InputLabel id="unit-future-label">Unité</InputLabel>
                  <Select
                    required={includeFuture}
                    labelId="unit-future-label"
                    value={unitFuture}
                    label="Unité"
                    onChange={(e) => {
                      const newUnit = e.target
                        .value as RelativeTimeRangeConfig['unit'];
                      setUnitFuture(newUnit);
                      handleChange(quantityFuture, newUnit, true);
                    }}
                  >
                    <MenuItem value="millisecondes">Milliseconde(s)</MenuItem>
                    <MenuItem value="seconds">Seconde(s)</MenuItem>
                    <MenuItem value="minutes">Minute(s)</MenuItem>
                    <MenuItem value="hours">Heure(s)</MenuItem>
                    <MenuItem value="days">Jour(s)</MenuItem>
                    <MenuItem value="weeks">Semaine(s)</MenuItem>
                    <MenuItem value="months">Mois</MenuItem>
                    <MenuItem value="years">Année(s)</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    </Box>
  );
}
